export type Role = 'coordenador' | 'dev' | 'po' | 'analista' | 'cto';

export interface Person {
  id: string;
  name: string;
  role: Role;
  level?: string;
  avatar?: string;
}

export interface Partner {
  name: string;
  type: 'canal' | 'erp' | 'operadora';
}

export interface Process {
  id: string;
  title: string;
  description: string;
  steps: string[];
  owner: Role;
}

export interface KeyResult {
  id: string;
  description: string;
  current: number;
  target: number;
  unit?: string;
  lowerIsBetter?: boolean;
}

export interface Objective {
  id: string;
  title: string;
  keyResults: KeyResult[];
}

export interface Tribe {
  id: string;
  name: string;
  tagline: string;
  color: 'teal' | 'amber';
  members: {
    devs: Person[];
    po: Person[];
    analistas: Person[];
  };
  partners: Partner[];
  processes: Process[];
  okrs: Objective[];
}

export interface OrgData {
  cto: Person;
  head?: Person;
  coordinator: Person;
  cycle: string;
  tribes: Tribe[];
}

export const data: OrgData = {
  cycle: 'OKR 6.2 · Abr–Jun',
  cto: {
    id: 'cto',
    name: 'CTO',
    role: 'cto',
    level: 'Direção',
  },
  coordinator: {
    id: 'coord-1',
    name: 'Guilherme Valério',
    role: 'coordenador',
    level: 'Coordenador de Integrações',
  },
  tribes: [
    {
      id: 'cloud',
      name: 'WhatsApp Cloud API',
      tagline: 'Ativação, qualidade e sustentação do canal WhatsApp',
      color: 'teal',
      members: {
        devs: [
          { id: 'dev-c1', name: 'A definir', role: 'dev', level: 'Dev Cloud — hoje no time geral' },
        ],
        po: [
          { id: 'po-c1', name: 'A definir', role: 'po', level: 'PO — hoje compartilhado' },
        ],
        analistas: [
          { id: 'ana-c1', name: 'Victor', role: 'analista', level: 'Analista de Integrações' },
          { id: 'ana-c2', name: 'Ricardo', role: 'analista', level: 'Analista de Integrações' },
          { id: 'ana-c3', name: 'Tayna', role: 'analista', level: 'Analista de Integrações' },
        ],
      },
      partners: [
        { name: 'Meta', type: 'canal' },
        { name: 'WhatsApp', type: 'canal' },
        { name: 'Dialog', type: 'operadora' },
        { name: 'Vonage', type: 'operadora' },
        { name: 'YCloud', type: 'operadora' },
      ],
      processes: [
        {
          id: 'cloud-p1',
          title: 'Ativação Cloud API',
          description: 'Processo de migração e ativação de clientes na WhatsApp Cloud API',
          steps: [
            'Verificação de elegibilidade do número',
            'Configuração do Business Manager (BM)',
            'Ativação do número na Cloud API',
            'Testes de envio e recebimento',
            'Handoff para CS com documentação',
          ],
          owner: 'analista',
        },
        {
          id: 'cloud-p2',
          title: 'Gestão de qualidade Meta',
          description: 'Monitoramento contínuo da conta Meta e qualidade do canal',
          steps: [
            'Monitoramento diário do health score',
            'Gestão de templates aprovados',
            'Tratativa de bloqueios e restrições',
            'Relacionamento com parceiro BSP',
            'Relatório mensal de qualidade',
          ],
          owner: 'coordenador',
        },
        {
          id: 'cloud-p3',
          title: 'Suporte N3 — Canal',
          description: 'Resolução de incidentes críticos que CS e N2 não conseguem resolver',
          steps: [
            'Recebimento do escalamento do CS',
            'Diagnóstico técnico do incidente',
            'Acionamento do parceiro se necessário',
            'Resolução e documentação',
            'Retorno ao CS com plano de ação',
          ],
          owner: 'coordenador',
        },
      ],
      okrs: [
        {
          id: 'cloud-o1',
          title: 'Expandir e consolidar a base Cloud API',
          keyResults: [
            {
              id: 'cloud-o1-kr1',
              description: 'Ativar novos números na Cloud API',
              current: 78,
              target: 120,
              unit: 'números',
            },
            {
              id: 'cloud-o1-kr2',
              description: 'Taxa de sucesso de ativação',
              current: 91,
              target: 95,
              unit: '%',
            },
            {
              id: 'cloud-o1-kr3',
              description: 'Tempo médio de ativação',
              current: 6.4,
              target: 5,
              unit: 'dias',
              lowerIsBetter: true,
            },
          ],
        },
        {
          id: 'cloud-o2',
          title: 'Garantir qualidade e estabilidade do canal',
          keyResults: [
            {
              id: 'cloud-o2-kr1',
              description: 'Health score médio Meta',
              current: 84,
              target: 90,
              unit: 'pts',
            },
            {
              id: 'cloud-o2-kr2',
              description: 'Incidentes N3 resolvidos em até 8h',
              current: 87,
              target: 100,
              unit: '%',
            },
            {
              id: 'cloud-o2-kr3',
              description: 'Alertas Meta respondidos em até 2h',
              current: 89,
              target: 100,
              unit: '%',
            },
          ],
        },
      ],
    },
    {
      id: 'erp',
      name: 'Gestão de Pedidos (ERP)',
      tagline: 'Implantação e sustentação das integrações de ERP e módulo de pedidos',
      color: 'amber',
      members: {
        devs: [
          { id: 'dev-e1', name: 'A definir', role: 'dev', level: 'Dev ERP — hoje no time geral' },
        ],
        po: [
          { id: 'po-e1', name: 'A definir', role: 'po', level: 'PO — hoje compartilhado' },
        ],
        analistas: [
          { id: 'ana-e1', name: 'A definir', role: 'analista', level: 'Analista ERP — onboarding' },
        ],
      },
      partners: [
        { name: 'Trier', type: 'erp' },
        { name: 'Alpha7', type: 'erp' },
        { name: 'Consys', type: 'erp' },
        { name: 'Inova', type: 'erp' },
        { name: 'VTEX', type: 'erp' },
        { name: 'Kruzer', type: 'erp' },
      ],
      processes: [
        {
          id: 'erp-p1',
          title: 'Onboarding ERP',
          description: 'Implantação do módulo de pedidos integrado ao ERP do cliente',
          steps: [
            'Mapeamento do ERP e fluxo de pedidos do cliente',
            'Configuração da integração na plataforma',
            'Testes com pedidos reais em homologação',
            'Go-live com acompanhamento do analista',
            'Handoff para CS após estabilização',
          ],
          owner: 'analista',
        },
        {
          id: 'erp-p2',
          title: 'Comunicação com parceiros ERP',
          description: 'Gestão do relacionamento técnico com os ERPs parceiros',
          steps: [
            'Alinhamento de roadmap com parceiro',
            'Homologação de novas versões',
            'Tratativa de bugs e inconsistências',
            'Documentação de mudanças de API',
            'Repasse ao time de dev',
          ],
          owner: 'coordenador',
        },
      ],
      okrs: [
        {
          id: 'erp-o1',
          title: 'Acelerar onboarding de ERPs',
          keyResults: [
            {
              id: 'erp-o1-kr1',
              description: 'Onboardings ERP concluídos no ciclo',
              current: 9,
              target: 15,
              unit: 'clientes',
            },
            {
              id: 'erp-o1-kr2',
              description: 'Tempo médio de onboarding',
              current: 13.5,
              target: 10,
              unit: 'dias úteis',
              lowerIsBetter: true,
            },
            {
              id: 'erp-o1-kr3',
              description: 'NPS do processo de integração',
              current: 7.2,
              target: 8.0,
              unit: 'pts',
            },
          ],
        },
        {
          id: 'erp-o2',
          title: 'Fortalecer ecossistema de parceiros',
          keyResults: [
            {
              id: 'erp-o2-kr1',
              description: 'ERPs parceiros com versão homologada no ciclo',
              current: 2,
              target: 4,
              unit: 'parceiros',
            },
            {
              id: 'erp-o2-kr2',
              description: 'Parceiros com documentação de API atualizada',
              current: 3,
              target: 6,
              unit: 'parceiros',
            },
          ],
        },
      ],
    },
  ],
};
